import React from 'react';
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';

import { getPayoutRequestDetail, confirmPayoutRequest } from 'api/payoutrequests';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';
import ImageViewer from 'components/ImageViewer/ImageViewer';
import PayoutMessageBox from 'components/PayoutMessageBox/PayoutMessageBox';

const PayoutRequest: React.FC = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { payoutRequestId } = useParams();

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

    const detailQuery = useQuery(['payoutrequest', payoutRequestId], getPayoutRequestDetail, {
        enabled: !!payoutRequestId,
    });

    const confirmMutation = useMutation(() => confirmPayoutRequest(payoutRequestId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['payoutrequest', payoutRequestId]);
        }
    });

    const d = detailQuery.data;
    const snapshot = d?.request_snapshot;
    const summary = snapshot?.summary;
    const rooms = snapshot?.rooms;

    return (
        <>
            <div className="col-span-12">
                <div className="w-full h-full bg-white shadow-md p-5 rounded" style={{ minHeight: 500 }}>
                    {detailQuery.isLoading ? (
                        <div className="flex items-center justify-center h-full" style={{ minHeight: 500 }}>
                            <CircularProgress />
                        </div>
                    ) : (<>
                    <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start mb-8">
                        <div className="shrink-0">
                            <p className="text-lg font-medium mb-4">Запрос на расчёт №{payoutRequestId}</p>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                <p className="text-lg font-light">Дата создания:</p>
                                <p className="text-lg font-light">{moment(d?.created_datetime).format('DD MMM YYYY HH:mm')}</p>
                                <p className="text-lg font-light">Статус:</p>
                                <p className="text-lg font-light"><span className={`px-2 rounded ${d?.processed ? 'bg-green-200' : 'bg-amber-500'}`} style={{ paddingTop: '1.2px', paddingBottom: '1.2px' }}>{d?.processed ? 'processed' : 'pending'}</span></p>
                                <p className="text-lg font-light invisible">-</p>
                                <p className="text-lg font-light invisible">-</p>
                                <p className="text-lg font-light">Игрок:</p>
                                <p className="text-lg font-light">{d?.user_username}</p>
                                {summary && (<>
                                    <p className="text-lg font-light">ABI (30 дней):</p>
                                    <p className="text-lg font-light">${summary.abi_30days}</p>
                                    <p className="text-lg font-light">Выплата:</p>
                                    <p className="text-lg font-light">{summary.total_payout_percent}%</p>
                                </>)}
                            </div>

                            {summary && (
                                <div className="mt-8">


                                    <p className="text-lg font-medium mb-4">Пакет</p>
                                    <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                        <p className="text-lg font-light">Результат пакета:</p>
                                        <p className="text-lg font-light">{summary.bundle?.result}</p>
                                        <p className="text-lg font-light">Результат игрока:</p>
                                        <p className="text-lg font-light">{summary.bundle?.player_result}</p>
                                        <p className="text-lg font-light">Профит SharkScope:</p>
                                        <p className="text-lg font-light">{summary.bundle?.sharkscope_profit}</p>
                                        <p className="text-lg font-light">Профит по балансам:</p>
                                        <p className="text-lg font-light">{summary.bundle?.balance_profit}</p>
                                        <p className="text-lg font-light">Профит NOS:</p>
                                        <p className="text-lg font-light">{summary.bundle?.nos_profit}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex-1">
                            {summary && (
                                <div className="mb-8">
                                    <p className="text-lg font-medium mb-4">Контракт</p>
                                    <div className="grid grid-cols-2 gap-x-8 gap-y-1" style={{ maxWidth: 'fit-content' }}>
                                        <p className="text-lg font-light">Профит SharkScope:</p>
                                        <p className="text-lg font-light">{summary.contract?.sharkscope_profit}</p>
                                        <p className="text-lg font-light">Профит по балансам:</p>
                                        <p className="text-lg font-light">{summary.contract?.balance_profit}</p>
                                        <p className="text-lg font-light">Профит NOS:</p>
                                        <p className="text-lg font-light">{summary.contract?.nos_profit}</p>
                                        <p className="text-lg font-light">Турниров всего:</p>
                                        <p className="text-lg font-light">{summary.contract?.entries_total}</p>
                                        <p className="text-lg font-light">Турниров отслежено:</p>
                                        <p className="text-lg font-light">{summary.contract?.entries_tracked}</p>
                                        <p className="text-lg font-light">Турниров NOS:</p>
                                        <p className="text-lg font-light">{summary.contract?.entries_nos}</p>
                                    </div>
                                </div>
                            )}

                            {rooms && rooms.length > 0 && (
                                <div>
                                    <p className="text-lg font-medium mb-4">Балансы по румам</p>
                                    <div className="overflow-auto">
                                        <table className="w-full text-left text-lg font-light" style={{ borderSpacing: '0 14px', borderCollapse: 'separate' }}>
                                            <thead>
                                                <tr className="border-b-2 border-gray-200">
                                                    <th className="py-0.5 pr-4 font-medium">Рум</th>
                                                    <th className="py-0.5 pr-4 font-medium">Ник в руме</th>
                                                    <th className="py-0.5 pr-4 font-medium text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>Баланс</th>
                                                    <th className="py-0.5 pr-4 font-medium text-right">Скриншот</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rooms.map((room: any) => (
                                                    <tr key={room.account_record_id} className="border-b border-gray-100">
                                                        <td className="py-0.5 pr-4">{room.room_name}</td>
                                                        <td className="py-0.5 pr-4">{room.account_record_name}</td>
                                                        <td className="py-0.5 pr-4 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{room.balance}</td>
                                                        <td className="py-0.5 pr-4" style={{ textAlign: 'right' }}>
                                                            <div style={{ display: 'inline-flex', gap: 80, justifyContent: 'flex-end' }}>
                                                                {[room.screenshot, room.screenshot2].filter(Boolean).map((url, i, arr) => (
                                                                    <ImageViewer
                                                                        key={i}
                                                                        url={url}
                                                                        label="Скрин"
                                                                        overlay={
                                                                            <>
                                                                                <span>Рум: <b>{room.room_name}</b></span>
                                                                                <span style={{ margin: '0 12px' }}>·</span>
                                                                                <span>Ник в руме: <b>{room.account_record_name}</b></span>
                                                                                <span style={{ margin: '0 12px' }}>·</span>
                                                                                <span>Баланс: <b>{room.balance}</b></span>
                                                                            </>
                                                                        }
                                                                    />
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                    {summary && d?.processed && (
                        <PayoutMessageBox username={d?.user_username} summary={summary} />
                    )}

                    {d?.data && !snapshot && (
                        <div className="mt-6 mb-4">
                            <p className="text-lg font-medium mb-4">Данные запроса</p>
                            <pre className="bg-gray-50 p-4 rounded text-sm overflow-auto max-h-96">
                                {JSON.stringify(d.data, null, 2)}
                            </pre>
                        </div>
                    )}

                    <div className="pt-8 space-x-4">
                        {!d?.processed && (
                            <Button
                                variant="contained"
                                color="success"
                                loading={confirmMutation.isLoading}
                                onClick={() => confirmMutation.mutate()}
                            >
                                Подтвердить
                            </Button>
                        )}
                        {!d?.processed && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                Удалить
                            </Button>
                        )}
                    </div>
                    </>)}
                </div>
            </div>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                payoutRequestId={payoutRequestId!}
                onClose={() => setDeleteDialogOpen(false)}
            />
        </>
    );
};

export default PayoutRequest;
